/**
 * PostHog product analytics — Round 10 corrected minimal subset.
 *
 * PR #5 (posthog/instrumentation-6b2d00) was reviewed and NOT merged.
 * Rejected from that PR:
 * - Throws in DEV when VITE_POSTHOG_* missing (breaks local startup)
 * - Autocapture / exception autocapture without content masking
 * - Broad events (chat_message_sent, company_context_saved, OAuth clicks, …)
 * - identify() with email/name PII
 * - No session-replay input masking
 * - No allowlist — would become a second content-bearing event stream
 *
 * This module:
 * - Initializes PostHog at most once
 * - Sources token + host from VITE_POSTHOG_KEY / VITE_POSTHOG_HOST
 * - No-ops when config is absent (app still starts)
 * - Enables session replay input/text masking when replay is on
 * - Only allows the Round 10 product-event allowlist with bounded props
 * - Never sends user/assistant/prompt/plan/tool/canvas content
 */

import posthogJs from 'posthog-js';

const ALLOWED_EVENTS = Object.freeze([
  'agent_create_requested',
  'agent_plan_presented',
  'agent_approval_requested',
  'agent_approval_submitted',
  'agent_question_requested',
  'agent_turn_failed',
  'artifact_authorized',
  'artifact_applied',
  'artifact_apply_failed',
]);

const ALLOWED_PROP_KEYS = Object.freeze([
  'agent_profile',
  'route_family',
  'terminal_status',
  'reason_code',
  'stage',
  'duration_bucket',
  'fallback_occurred',
  'deployment_commit',
]);

const posthogKey = import.meta.env.VITE_POSTHOG_KEY;
const posthogHost = import.meta.env.VITE_POSTHOG_HOST;

let initialized = false;

function initPostHogOnce() {
  if (initialized) return initialized;
  if (!posthogKey || !posthogHost) {
    // Absent config must not break startup (local or production frontend).
    return false;
  }

  posthogJs.init(String(posthogKey), {
    api_host: String(posthogHost),
    capture_pageview: false,
    capture_pageleave: false,
    autocapture: false,
    disable_session_recording: false,
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: '*',
      recordCrossOriginIframes: false,
    },
    // Never persist raw network bodies that could include auth headers.
    advanced_disable_decide: false,
  });

  initialized = true;
  return true;
}

initPostHogOnce();

/**
 * Capture an allowlisted product event with bounded categorical properties only.
 * Silently drops unknown events / properties — never throws.
 */
export function captureProductEvent(eventName, properties = {}) {
  if (!ALLOWED_EVENTS.includes(eventName)) {
    if (import.meta.env.DEV) {
      console.warn(`[posthog] rejected non-allowlisted event: ${eventName}`);
    }
    return false;
  }
  if (!initialized && !initPostHogOnce()) {
    return false;
  }

  const safeProps = {};
  for (const key of ALLOWED_PROP_KEYS) {
    if (properties[key] === undefined || properties[key] === null) continue;
    const value = properties[key];
    if (typeof value === 'string' || typeof value === 'boolean' || typeof value === 'number') {
      // Bound string length to avoid accidental content payloads.
      safeProps[key] = typeof value === 'string' ? value.slice(0, 64) : value;
    }
  }

  try {
    posthogJs.capture(eventName, safeProps);
    return true;
  } catch {
    return false;
  }
}

/** Distinct-id only — never email/name/picture. */
export function identifyUser(distinctId) {
  if (!distinctId || (!initialized && !initPostHogOnce())) return;
  try {
    posthogJs.identify(String(distinctId));
  } catch {
    /* no-op */
  }
}

export function resetAnalytics() {
  if (!initialized) return;
  try {
    posthogJs.reset();
  } catch {
    /* no-op */
  }
}

export function isPostHogEnabled() {
  return initialized;
}

export const POSTHOG_ALLOWED_EVENTS = ALLOWED_EVENTS;
export const POSTHOG_ALLOWED_PROP_KEYS = ALLOWED_PROP_KEYS;

// Default export stays API-compatible with a soft no-op surface for callers
// that expect posthog.capture / posthog.reset without going through allowlist.
const posthog = {
  capture: captureProductEvent,
  identify: identifyUser,
  reset: resetAnalytics,
  get isEnabled() {
    return initialized;
  },
};

export default posthog;
